// src/stores/appointment.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  Adviser,
  Vehicle,
  Hour,
  AppointmentDate,
  AvailabilityHoursByDateMap,
  Appointment,
  QueriedVehicleSlugs,
  AppointmentItem
} from '~concierge/types/AppointmentTypes'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { trackUserSelection } from '~conciergeUiCommons/utils/analytics'

const APPOINTMENT_INITIAL_STATE = {
  flow: null,
  date: null,
  adviser: null,
  hour: null,
  vehicle: null
}

export const useAppointmentStore = defineStore({
  id: 'appointment',
  state: () => ({
    loadingDates: ref<boolean>(false),
    loadingVehicles: ref<boolean>(false),
    loadingHours: ref<boolean>(false),
    loadingAdvisers: ref<boolean>(false),
    availableVehicles: [] as Vehicle[],
    availableDates: new Map([]) as AvailabilityHoursByDateMap,
    disabledDates: [] as AppointmentDate[],
    availableAdvisers: [] as Adviser[],
    selectedDateHours: [] as Hour[],
    selectedDate: null as AppointmentDate | null,
    selectedVehicles: [] as Vehicle[],
    vehicleAvailabilityMessage: '' as string,
    queriedVehicleSlugs: {} as QueriedVehicleSlugs,
    hasMoreVehicles: false,
    selectedAdviser: null as Adviser | null,
    selectedHour: null as Hour | null,
    showUsedAdviser: ref<boolean>(false),
    lockShowUsedAdviser: ref<boolean>(false),
    appointment: {
      flow: null,
      date: null,
      adviser: null,
      hour: null,
      vehicles: [] as Vehicle[],
      confirmed: false
    } as Appointment
  }),
  getters: {
    getAppointmentItem(state): AppointmentItem {
      if (state.appointment.flow === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
        const parts = []
        let appointmentPhoto
        if (state.appointment.vehicles.length > 0) {
          const identityStore = useIdentityStore()
          const showVehicleBrand = identityStore?.identity?.settings?.showVehicleBrand
          const { brand, model, trim, year, photo } = state.appointment.vehicles[0]
          if (brand && showVehicleBrand) parts.push(brand)
          if (model) parts.push(model)
          if (trim) parts.push(trim)
          if (year) parts.push(year)
          appointmentPhoto = photo
        }

        return {
          photo: appointmentPhoto,
          name: parts.join(' ')
        }
      }
      return {
        photo: state.appointment.adviser?.photo,
        name: state.appointment.adviser?.first_name + ' ' + state.appointment.adviser?.last_name
      }
    },
    isAppointmentConfirmed(state): boolean {
      return this.isAppointmentSet && state.appointment.confirmed
    },
    isAppointmentSet(state): boolean {
      if (!state.appointment.flow) {
        return false
      }
      if (state.appointment.flow === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
        return (
          state.appointment.vehicles !== null &&
          Object.keys(state.appointment.vehicles).length > 0 &&
          state.appointment.hour !== null &&
          state.appointment.date !== null
        )
      }
      if (
        state.appointment.flow === ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment ||
        state.appointment.flow === ConciergeAppointmentQuestionFlow.FreeAppointment
      ) {
        return (
          state.appointment.adviser !== null &&
          Object.keys(state.appointment.adviser).length > 0 &&
          state.appointment.hour !== null &&
          state.appointment.date !== null
        )
      } else {
        console.warn('Appointment not set')
      }
      return false
    },
    buildAppointmentForSubmission(state): Record<string, string | Vehicle[] | null> {
      return {
        date: state.appointment.date,
        time: state.appointment.hour,
        vehicles: state.appointment.vehicles,
        adviser_id: state.appointment.adviser?.id.toString() ?? ''
      }
    },
    hasNewAndUsedAdvisers(): boolean {
      return this.hasNewAdvisers && this.hasUsedAdvisers
    },
    hasUsedAdvisers(state): boolean {
      return state.availableAdvisers.some(adviser => adviser.roles.includes('used'))
    },
    hasNewAdvisers(state): boolean {
      return state.availableAdvisers.some(adviser => adviser.roles.includes('new'))
    }
  },
  actions: {
    setAvailableAdvisers(availableAdvisers: Adviser[]): void {
      this.availableAdvisers = availableAdvisers
      if (this.lockShowUsedAdviser) {
        return
      }
      this.showUsedAdviser = false
      if (!this.hasNewAndUsedAdvisers) {
        if (this.hasUsedAdvisers) {
          this.showUsedAdviser = true
        } else if (this.hasNewAdvisers) {
          this.showUsedAdviser = false
        }
      }
    },
    confirmAppointment(): void {
      if (this.isAppointmentSet) {
        this.appointment.confirmed = true
      } else {
        console.error('Appointment not set')
      }
    },
    setAppointment(appointment: Appointment) {
      trackUserSelection(appointment.flow || 'unknown', this.getFormType())
      Object.assign(this.appointment, appointment)
    },
    getFormType(): 'form' | 'max' | 'direct' {
      switch (this.service) {
        case 'conciergeForm':
          return 'form'
        case 'conciergeEvent':
          return 'max'
        case 'conciergeEmbed':
          return 'direct'
        default:
          return 'form'
      }
    },
    resetAppointment() {
      Object.assign(this.appointment, APPOINTMENT_INITIAL_STATE)
    },
    resetAvailableDates() {
      this.availableDates.clear()
    },
    resetDisabledDates() {
      this.disabledDates = []
    },
    resetAvailableAdvisers() {
      this.setAvailableAdvisers([])
    },
    resetAvailableVehicles() {
      this.availableVehicles = []
    },
    resetSelectedDateHours() {
      this.selectedDateHours = []
    },
    resetSelectedDate() {
      this.selectedDate = null
    },
    resetSelectedAdviser() {
      this.selectedAdviser = null
    },
    resetSelectedVehicles() {
      this.selectedVehicles = []
    },
    resetSelectedHour() {
      this.selectedHour = null
    },

    resetData() {
      this.resetAvailableDates()
      this.resetAvailableVehicles()
      this.resetDisabledDates()
      this.resetAvailableAdvisers()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedAdviser()
      this.resetSelectedVehicles()
      this.resetSelectedHour()
      this.resetAppointment()
    },
    resetDataExceptVehiclesList() {
      this.resetAvailableDates()
      this.resetDisabledDates()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedHour()
      this.resetAppointment()
    },
    resetDataExceptAdvisersList() {
      this.resetAvailableDates()
      this.resetDisabledDates()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedAdviser()
      this.resetSelectedHour()
      this.resetAppointment()
    },
    addVehicle(vehicle: Vehicle): void {
      if (this.selectedVehicles.some(x => x.id === vehicle.id)) {
        this.selectedVehicles = this.selectedVehicles.filter(x => x.id !== vehicle.id)
      } else {
        if (this.selectedVehicles.length >= 3) {
          return
        }
        this.selectedVehicles = [...this.selectedVehicles, vehicle]
      }
    }
  }
})
